/**
 * MockDataProvider
 * In-memory implementation of IDataProvider for development and testing
 */

import { nanoid } from 'nanoid'
import type {
  Book,
  CreateBookDto,
  UpdateBookDto,
  Chapter,
  CreateChapterDto,
  UpdateChapterDto,
  Content,
  ExportOptions,
  ExportResult,
} from '@/core/models'
import type { IDataProvider, BookStatistics } from '@/core/interfaces'
import { sampleBooks, sampleChapters, sampleContent } from './mockData'

export class MockDataProvider implements IDataProvider {
  private books: Map<string, Book>
  private chapters: Map<string, Chapter>
  private content: Map<string, Content>
  private simulateDelay: boolean

  constructor(simulateDelay = true) {
    // Initialize with sample data
    this.books = new Map(sampleBooks.map(book => [book.id, { ...book }]))
    this.chapters = new Map(sampleChapters.map(chapter => [chapter.id, { ...chapter }]))
    this.content = new Map(sampleContent)
    this.simulateDelay = simulateDelay
  }

  // ==================== Helper Methods ====================

  private async delay(ms = 100): Promise<void> {
    if (this.simulateDelay) {
      await new Promise(resolve => setTimeout(resolve, ms))
    }
  }

  private calculateWordCount(text: string): number {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length
  }

  // ==================== Book Operations ====================

  async getBooks(): Promise<Book[]> {
    await this.delay()
    return Array.from(this.books.values()).sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
    )
  }

  async getBook(id: string): Promise<Book> {
    await this.delay()
    const book = this.books.get(id)
    if (!book) {
      throw new Error(`Book with id "${id}" not found`)
    }
    return { ...book }
  }

  async createBook(dto: CreateBookDto): Promise<Book> {
    await this.delay(200)

    const now = new Date()
    const book: Book = {
      id: nanoid(),
      title: dto.title,
      author: dto.author,
      description: dto.description || '',
      createdAt: now,
      updatedAt: now,
      status: 'draft',
      metadata: {
        language: dto.language || 'en',
        genre: dto.genre || [],
        tags: dto.tags || [],
        wordCount: 0,
      },
      chapterIds: [],
    }

    this.books.set(book.id, book)
    return { ...book }
  }

  async updateBook(id: string, updates: UpdateBookDto): Promise<Book> {
    await this.delay(150)

    const book = this.books.get(id)
    if (!book) {
      throw new Error(`Book with id "${id}" not found`)
    }

    const updatedBook: Book = {
      ...book,
      ...updates,
      metadata: {
        ...book.metadata,
        ...updates.metadata,
      },
      updatedAt: new Date(),
    }

    this.books.set(id, updatedBook)
    return { ...updatedBook }
  }

  async deleteBook(id: string): Promise<void> {
    await this.delay(150)

    const book = this.books.get(id)
    if (!book) {
      throw new Error(`Book with id "${id}" not found`)
    }

    // Delete all chapters
    for (const chapterId of book.chapterIds) {
      this.chapters.delete(chapterId)
      this.content.delete(chapterId)
    }

    this.books.delete(id)
  }

  // ==================== Chapter Operations ====================

  async getChapters(bookId: string): Promise<Chapter[]> {
    await this.delay()

    const book = this.books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    const chapters = book.chapterIds
      .map(id => this.chapters.get(id))
      .filter((chapter): chapter is Chapter => chapter !== undefined)
      .sort((a, b) => a.order - b.order)

    return chapters.map(chapter => ({ ...chapter }))
  }

  async getChapter(id: string): Promise<Chapter> {
    await this.delay()

    const chapter = this.chapters.get(id)
    if (!chapter) {
      throw new Error(`Chapter with id "${id}" not found`)
    }

    return { ...chapter }
  }

  async createChapter(dto: CreateChapterDto): Promise<Chapter> {
    await this.delay(200)

    const book = this.books.get(dto.bookId)
    if (!book) {
      throw new Error(`Book with id "${dto.bookId}" not found`)
    }

    const now = new Date()
    const order = dto.order ?? book.chapterIds.length + 1

    const chapter: Chapter = {
      id: nanoid(),
      bookId: dto.bookId,
      title: dto.title,
      order,
      content: dto.content || '',
      wordCount: this.calculateWordCount(dto.content || ''),
      status: 'draft',
      createdAt: now,
      updatedAt: now,
    }

    this.chapters.set(chapter.id, chapter)

    // Initialize content
    const content: Content = {
      chapterId: chapter.id,
      text: chapter.content,
      formatting: [],
      version: 1,
      lastSaved: now,
      autoSaveEnabled: true,
    }
    this.content.set(chapter.id, content)

    // Add to book
    book.chapterIds.push(chapter.id)
    book.updatedAt = now
    this.books.set(book.id, book)

    return { ...chapter }
  }

  async updateChapter(id: string, updates: UpdateChapterDto): Promise<Chapter> {
    await this.delay(150)

    const chapter = this.chapters.get(id)
    if (!chapter) {
      throw new Error(`Chapter with id "${id}" not found`)
    }

    const updatedChapter: Chapter = {
      ...chapter,
      ...updates,
      wordCount: updates.content
        ? this.calculateWordCount(updates.content)
        : chapter.wordCount,
      updatedAt: new Date(),
    }

    this.chapters.set(id, updatedChapter)

    // Update book's updatedAt
    const book = this.books.get(chapter.bookId)
    if (book) {
      book.updatedAt = new Date()
      this.books.set(book.id, book)
    }

    return { ...updatedChapter }
  }

  async deleteChapter(id: string): Promise<void> {
    await this.delay(150)

    const chapter = this.chapters.get(id)
    if (!chapter) {
      throw new Error(`Chapter with id "${id}" not found`)
    }

    // Remove from book
    const book = this.books.get(chapter.bookId)
    if (book) {
      book.chapterIds = book.chapterIds.filter(chapterId => chapterId !== id)
      book.updatedAt = new Date()
      this.books.set(book.id, book)
    }

    this.chapters.delete(id)
    this.content.delete(id)
  }

  async reorderChapters(bookId: string, chapterIds: string[]): Promise<Chapter[]> {
    await this.delay(150)

    const book = this.books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    // Update chapter orders
    const updatedChapters: Chapter[] = []
    chapterIds.forEach((chapterId, index) => {
      const chapter = this.chapters.get(chapterId)
      if (chapter && chapter.bookId === bookId) {
        const updatedChapter = {
          ...chapter,
          order: index + 1,
          updatedAt: new Date(),
        }
        this.chapters.set(chapterId, updatedChapter)
        updatedChapters.push(updatedChapter)
      }
    })

    // Update book
    book.chapterIds = chapterIds
    book.updatedAt = new Date()
    this.books.set(bookId, book)

    return updatedChapters
  }

  // ==================== Content Operations ====================

  async getContent(chapterId: string): Promise<Content> {
    await this.delay()

    const content = this.content.get(chapterId)
    if (!content) {
      const chapter = this.chapters.get(chapterId)
      if (!chapter) {
        throw new Error(`Chapter with id "${chapterId}" not found`)
      }

      // Create default content if doesn't exist
      const newContent: Content = {
        chapterId,
        text: chapter.content,
        formatting: [],
        version: 1,
        lastSaved: new Date(),
        autoSaveEnabled: true,
      }
      this.content.set(chapterId, newContent)
      return { ...newContent }
    }

    return { ...content }
  }

  async updateContent(chapterId: string, updates: Partial<Content>): Promise<Content> {
    await this.delay(100)

    let content = this.content.get(chapterId)
    if (!content) {
      const chapter = this.chapters.get(chapterId)
      if (!chapter) {
        throw new Error(`Chapter with id "${chapterId}" not found`)
      }

      content = {
        chapterId,
        text: '',
        formatting: [],
        version: 1,
        lastSaved: new Date(),
        autoSaveEnabled: true,
      }
    }

    const updatedContent: Content = {
      ...content,
      ...updates,
      version: content.version + 1,
      lastSaved: new Date(),
    }

    this.content.set(chapterId, updatedContent)

    // Update chapter content and word count
    if (updates.text !== undefined) {
      const chapter = this.chapters.get(chapterId)
      if (chapter) {
        chapter.content = updates.text
        chapter.wordCount = this.calculateWordCount(updates.text)
        chapter.updatedAt = new Date()
        this.chapters.set(chapterId, chapter)
      }
    }

    return { ...updatedContent }
  }

  // ==================== Search Operations ====================

  async searchBooks(query: string): Promise<Book[]> {
    await this.delay()

    const lowerQuery = query.toLowerCase()
    return Array.from(this.books.values()).filter(
      book =>
        book.title.toLowerCase().includes(lowerQuery) ||
        book.author.toLowerCase().includes(lowerQuery) ||
        book.description.toLowerCase().includes(lowerQuery),
    )
  }

  async searchChapters(bookId: string, query: string): Promise<Chapter[]> {
    await this.delay()

    const book = this.books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    const lowerQuery = query.toLowerCase()
    return Array.from(this.chapters.values())
      .filter(
        chapter =>
          chapter.bookId === bookId &&
          (chapter.title.toLowerCase().includes(lowerQuery) ||
            chapter.content.toLowerCase().includes(lowerQuery)),
      )
      .sort((a, b) => a.order - b.order)
  }

  // ==================== Export Operations ====================

  async exportBook(bookId: string, options: ExportOptions): Promise<ExportResult> {
    await this.delay(500) // Longer delay for export

    const book = this.books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    // For mock, just create a simple text blob
    const chapters = await this.getChapters(bookId)
    let text = `${book.title}\nby ${book.author}\n\n${book.description}\n\n`

    for (const chapter of chapters) {
      text += `\n\nChapter ${chapter.order}: ${chapter.title}\n\n${chapter.content}\n`
    }

    const blob = new Blob([text], { type: 'text/plain' })
    const filename = `${book.title.replace(/\s+/g, '_')}.${options.format}`

    return {
      success: true,
      blob,
      filename,
    }
  }

  // ==================== Utility Operations ====================

  async getBookWordCount(bookId: string): Promise<number> {
    await this.delay()

    const chapters = await this.getChapters(bookId)
    return chapters.reduce((total, chapter) => total + chapter.wordCount, 0)
  }

  async getBookStatistics(bookId: string): Promise<BookStatistics> {
    await this.delay()

    const book = this.books.get(bookId)
    if (!book) {
      throw new Error(`Book with id "${bookId}" not found`)
    }

    const chapters = await this.getChapters(bookId)
    const totalWords = chapters.reduce((total, chapter) => total + chapter.wordCount, 0)
    const totalCharacters = chapters.reduce(
      (total, chapter) => total + chapter.content.length,
      0,
    )
    const completedChapters = chapters.filter(c => c.status === 'completed').length
    const draftChapters = chapters.filter(c => c.status === 'draft').length

    return {
      totalWords,
      totalCharacters,
      totalChapters: chapters.length,
      averageWordsPerChapter: chapters.length > 0 ? Math.round(totalWords / chapters.length) : 0,
      estimatedReadingTime: Math.ceil(totalWords / 200), // Assuming 200 words per minute
      completedChapters,
      draftChapters,
    }
  }
}
